"use client";

import React, { useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import Button from "@/components/ui/Button";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useGetFormations } from "@/actions/formation";
import { ENTREPRENEURSHIP_CATEGORY_SLUG } from "@/lib/constants";


export default function Ideal() {
    const router = useRouter();
    const { formations, formationsLoading, formationsEmpty } = useGetFormations(
        ENTREPRENEURSHIP_CATEGORY_SLUG
    );

    // 2 cards per page on desktop, 1 on mobile — derived (no effect, hydration-safe).
    const cardsPerPage = useMediaQuery("(min-width: 768px)") ? 2 : 1;
    const [currentIndex, setCurrentIndex] = useState(0);

    const handleNext = () => {
        setCurrentIndex((prev) =>
            prev + cardsPerPage < formations.length ? prev + cardsPerPage : prev
        );
    };

    const handlePrev = () => {
        setCurrentIndex((prev) => Math.max(prev - cardsPerPage, 0));
    };

    // Nothing to feature once loaded — omit the whole section (title + CTA included).
    if (formationsEmpty) return null;

    const visibleFormations = formations.slice(currentIndex, currentIndex + cardsPerPage);
    const isFirstPage = currentIndex === 0;
    const isLastPage = currentIndex + cardsPerPage >= formations.length;

    return (
        <section className="relative w-full bg-[#C7F8FF]/30 pb-10.5 sm:pb-12 pt-10.5 sm:pt-27.5">
            <div
                className="absolute left-1/2 -translate-x-1/2 w-13.75 sm:w-28 h-13.75 sm:h-28 bg-white border-b border-[#40BFDE] rounded-[14px] sm:rounded-[30px] flex items-center justify-center -top-8"
                aria-hidden="true"
            >
                <Image
                    src="/assets/icons/launching.svg"
                    alt="Icone de lancement"
                    title="Icone de lancement"
                    width={100}
                    height={100}
                />
            </div>

            <div className="mx-auto px-4 max-w-7xl relative">
                {/* Title */}
                <h2 className="mb-4 sm:mb-11.5 text-center font-red-hat font-light text-[30px] md:text-[40px] leading-10 sm:leading-13.25 text-[#1B365E]">
                    Formations idéales pour se lancer et réussir dans
                    <br className="sm:block hidden" /> l&apos;entreprenariat
                </h2>

                <div className="flex flex-col md:flex-row gap-8 items-start relative">
                    {/* Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 flex-1">
                        {formationsLoading
                            ? Array.from({ length: cardsPerPage }).map((_, i) => (
                                <div key={i} className="flex flex-col h-full animate-pulse" aria-hidden="true">
                                    <div className="flex-1 space-y-4">
                                        <div className="h-12 lg:h-20 w-3/4 bg-gray-200 rounded" />
                                        <div className="h-4 w-1/3 bg-gray-200 rounded" />
                                        <div className="h-20 w-full bg-gray-200 rounded" />
                                    </div>
                                </div>
                            ))
                            : visibleFormations.map((formation) => (
                                <article key={formation.id} className="flex flex-col h-full">
                                    <div className="flex-1 text-left">
                                        <h3 className="font-red-hat font-light text-[20px] sm:text-[20px] lg:text-[30px] text-[#40BFDE] leading-6 sm:leading-8 lg:leading-12 sm:mb-0 mb-3">
                                            {formation.title}
                                        </h3>
                                        <p className="font-bold text-[#1B365E] text-[14px] md:text-[16px] leading-3.5 md:leading-5 sm:leading-6.5 mb-2.75">
                                            {formation.category}
                                        </p>
                                        <p className="text-[#1B365E] text-[12px] sm:text-[14px] leading-5 font-normal">
                                            {formation.description}
                                        </p>
                                    </div>

                                    {/* Card Actions */}
                                    <div className="flex items-center justify-center md:justify-start gap-11 mt-5 sm:mt-8.25">
                                        <button
                                            type="button"
                                            onClick={() => router.push(`/formation/${formation.slug}`)}
                                            className="flex cursor-pointer items-center gap-2 text-[#1B365E] font-bold text-[15px] hover:translate-x-0.5 transition-transform"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8ZM4.75 7.25C4.33579 7.25 4 7.58579 4 8C4 8.41421 4.33579 8.75 4.75 8.75L9.43934 8.75L8.21967 9.96967C7.92678 10.2626 7.92678 10.7374 8.21967 11.0303C8.51256 11.3232 8.98744 11.3232 9.28033 11.0303L11.7803 8.53033C12.0732 8.23744 12.0732 7.76256 11.7803 7.46967L9.28033 4.96967C8.98744 4.67678 8.51256 4.67678 8.21967 4.96967C7.92678 5.26256 7.92678 5.73744 8.21967 6.03033L9.43934 7.25L4.75 7.25Z" fill="#1B365E" />
                                            </svg>


                                            <span>Découvrir</span>
                                        </button>
                                        <Button
                                            variant="primary"
                                            onClick={() => router.push(`/formation/${formation.slug}`)}
                                            className="bg-[#1B365E] text-white text-[15px] w-27! h-9!"
                                        >
                                            S&apos;inscrire
                                        </Button>
                                    </div>
                                </article>
                            ))}
                    </div>

                    {/* Navigation Arrows */}
                    <div className="w-full md:w-fit flex flex-row md:flex-col gap-4 justify-center items-center mt-0 md:mt-5 md:ml-0 shrink-0">
                        <button
                            onClick={handlePrev}
                            disabled={isFirstPage}
                            className={`p-1.5 transition-all cursor-pointer order-1 md:order-2
                                 ${isFirstPage
                                    ? "opacity-20 cursor-not-allowed"
                                    : "text-[#1B365E]"
                                }`}
                            aria-label="Précédent"
                        >
                            <svg width="28" height="30" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0.585787 13.3138C-0.195263 14.0949 -0.195263 15.3612 0.585787 16.1422L13.3137 28.8702C14.0948 29.6512 15.3611 29.6512 16.1421 28.8702C16.9232 28.0891 16.9232 26.8228 16.1421 26.0417L4.82843 14.728L16.1421 3.41432C16.9232 2.63327 16.9232 1.36694 16.1421 0.585893C15.3611 -0.195156 14.0948 -0.195156 13.3137 0.585893L0.585787 13.3138ZM28 14.728L28 12.728L2 12.728L2 14.728L2 16.728L28 16.728L28 14.728Z" fill="#1B365E" />
                            </svg>
                        </button>
                        <button
                            onClick={handleNext}
                            disabled={isLastPage}
                            className={`p-1.5 transition-all cursor-pointer order-2 md:order-1
                                 ${isLastPage
                                    ? "opacity-20 cursor-not-allowed"
                                    : "text-[#1B365E]"
                                }`}
                            aria-label="Suivant"
                        >
                            <svg width="28" height="30" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M27.4142 16.1422C28.1953 15.3612 28.1953 14.0949 27.4142 13.3138L14.6863 0.585892C13.9052 -0.195157 12.6389 -0.195157 11.8579 0.585892C11.0768 1.36694 11.0768 2.63327 11.8579 3.41432L23.1716 14.728L11.8579 26.0417C11.0768 26.8228 11.0768 28.0891 11.8579 28.8702C12.6389 29.6512 13.9052 29.6512 14.6863 28.8702L27.4142 16.1422ZM0 14.728V16.728H26V14.728V12.728H0V14.728Z" fill="#1B365E" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Bottom CTA */}
                <div className="flex justify-center mt-6 sm:mt-14.25">
                    <Button
                        variant="gradient"
                        onClick={() => router.push("/entrepreneurship")}
                        className="sm:px-10! px-5!"
                    >
                        Toutes les formations de l&apos;entreprenariat
                    </Button>
                </div>
            </div>
        </section>
    );
}